import { useGetOrdersQuery, useRetryPosSyncMutation } from '@/store/api';
import {
  useGetOrderPaymentQuery,
  useGetOrdersNeedingAttentionQuery,
  useReconcileOrderMutation,
  useResendPaymentLinkMutation
} from '@/store/accountApi';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, EmptyState, ErrorState, Panel, SkeletonRows, Status } from '@/components/ui';
import { Link } from 'react-router-dom';
import { PosStatusBanner } from '@/features/integrations/PosStatusBanner';
import { formatDateTime, formatMoney, formatPhone } from '@/utils/format';

export function OrdersPage() {
  const dispatch = useAppDispatch();
  const { data, isLoading, isError, refetch } = useGetOrdersQuery({ limit: 25 });
  const [retry, { isLoading: retrying }] = useRetryPosSyncMutation();

  const resend = async (id: string) => {
    const result = await retry(id);
    dispatch(
      'error' in result
        ? pushToast('Could not send the order to your POS.', 'error')
        : pushToast('Order sent to your POS.', 'success')
    );
  };

  return (
    <div className="space-y-4">
      <h1 className="text-display">Orders</h1>

      <PosStatusBanner />

      <AttentionQueue />

      <Panel>
        {isError ? (
          <ErrorState message="Could not load orders." onRetry={refetch} />
        ) : isLoading ? (
          <SkeletonRows rows={8} />
        ) : data?.data.length === 0 ? (
          <EmptyState
            headline="No phone orders yet"
            hint="Orders taken over the phone land here, with their status in your POS."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-rule text-left text-micro text-slate">
                <th className="px-4 py-2 font-medium">Order</th>
                <th className="px-4 py-2 font-medium">Customer</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Placed</th>
                <th className="px-4 py-2 font-medium">Total</th>
                <th className="px-4 py-2 font-medium">POS</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {data?.data.map((order) => (
                <tr key={order._id} className="hover:bg-paper">
                  <td className="tabular px-4 py-3 text-small text-ink">#{order.orderNumber}</td>
                  <td className="px-4 py-3 text-small text-ink">
                    {order.customerName ?? '—'}
                    {order.customerPhone ? (
                      <span className="tabular block text-micro text-slate">
                        {formatPhone(order.customerPhone)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-small capitalize text-slate">
                    {order.fulfillment.toLowerCase()}
                  </td>
                  <td className="tabular px-4 py-3 text-small text-slate">
                    {formatDateTime(order.createdAt)}
                  </td>
                  <td className="tabular px-4 py-3 text-small text-ink">
                    {formatMoney(order.totalCents)}
                  </td>
                  <td className="px-4 py-3">
                    <Status value={order.status} />
                    {order.paidAt ? (
                      <span className="ml-2 rounded bg-jade-soft px-1.5 py-0.5 text-micro text-jade">Paid</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {order.status === 'POS_SYNC_FAILED' ? (
                      <Button
                        variant="secondary"
                        loading={retrying}
                        onClick={() => resend(order._id)}
                      >
                        Send again
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}


/**
 * Orders whose POS outcome was never established.
 *
 * The only action offered is Reconcile, which asks the POS whether it already
 * has the order before sending anything. There is deliberately no plain
 * "retry": on an ambiguous submission that is how one phone call becomes two
 * kitchen tickets.
 */
function AttentionQueue() {
  const dispatch = useAppDispatch();
  const { data } = useGetOrdersNeedingAttentionQuery();
  const [reconcile, { isLoading }] = useReconcileOrderMutation();

  const orders = data?.data ?? [];
  if (orders.length === 0) return null;

  const run = async (id: string) => {
    const result = await reconcile(id);

    if ('error' in result) {
      dispatch(pushToast('Could not reach your POS. The order still needs checking.', 'error'));
      return;
    }

    dispatch(
      result.data.adopted
        ? pushToast('Your POS already had this order. No second ticket was created.', 'success')
        : result.data.ok
          ? pushToast('Order sent to your POS.', 'success')
          : pushToast(
              'Still could not confirm. Check your POS for the reference before re-sending.',
              'error'
            )
    );
  };

  return (
    <Panel title="Orders that need checking">
      <p className="border-b border-rule px-4 py-2.5 text-small text-slate">
        These orders were taken over the phone but we could not confirm they reached your POS.
        Reconcile checks whether your POS already has the order before sending anything.
      </p>

      <ul className="divide-y divide-rule">
        {orders.map((order) => (
          <li key={order._id} className="px-4 py-3.5">
            <div className="flex flex-wrap items-center gap-3">
              <span className="tabular text-small text-ink">#{order.orderNumber}</span>
              <span className="text-small text-ink">{order.customerName ?? 'Phone order'}</span>
              {order.customerPhone ? (
                <span className="tabular text-micro text-slate">
                  {formatPhone(order.customerPhone)}
                </span>
              ) : null}
              <span className="tabular text-small text-ink">{formatMoney(order.totalCents)}</span>
              <Status value={order.status} />

              <div className="ml-auto flex items-center gap-3">
                {order.callId ? (
                  <Link
                    to={`/calls/${order.callId}`}
                    className="text-small text-jade underline underline-offset-2"
                  >
                    Call
                  </Link>
                ) : null}
                <Button loading={isLoading} onClick={() => run(order._id)}>
                  Reconcile with POS
                </Button>
              </div>
            </div>

            <dl className="mt-2 grid gap-x-6 gap-y-1 text-micro text-slate sm:grid-cols-2">
              <div>
                <dt className="inline">Items: </dt>
                <dd className="inline text-ink">{order.items.join(', ')}</dd>
              </div>
              <div>
                <dt className="inline">Taken: </dt>
                <dd className="inline">{formatDateTime(order.createdAt)}</dd>
              </div>
              <div>
                <dt className="inline">Search your POS for: </dt>
                <dd className="tabular inline text-ink">{order.externalReferenceId}</dd>
              </div>
              <div>
                <dt className="inline">Checks so far: </dt>
                <dd className="inline">{order.reconciliationAttempts}</dd>
              </div>
            </dl>

            {order.lastError ? (
              <p className="mt-1.5 rounded bg-rust-soft px-2.5 py-1.5 text-micro text-rust">
                {order.lastError}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}


/**
 * Payment state for one order.
 *
 * "Paid" and "POS reconciled" are shown as two separate rows on purpose. Money
 * arriving in our system does not mean the till agrees, and a restaurant that
 * conflates the two will close a shift with books that do not balance.
 */
export function OrderPaymentPanel({ orderId }: { orderId: string }) {
  const dispatch = useAppDispatch();
  const { data } = useGetOrderPaymentQuery(orderId);
  const [resend, { isLoading }] = useResendPaymentLinkMutation();

  const attempt = data?.attempts.at(-1);
  if (!data || !attempt) return null;

  const resendLink = async () => {
    const result = await resend(orderId);
    dispatch(
      'error' in result
        ? pushToast('Could not send the text. The same link is still valid.', 'error')
        : pushToast('Payment link sent again.', 'success')
    );
  };

  const reconciliationLabel: Record<string, string> = {
    RECONCILED: 'Recorded on the POS',
    FAILED: 'Could not record on the POS',
    PENDING: 'Recording on the POS',
    PENDING_PARTNER_CAPABILITY: 'Not available for this POS yet',
    NOT_SUPPORTED: 'This POS cannot record external payments',
    NOT_APPLICABLE: 'No POS connected'
  };

  const minutesLeft = attempt.expiresAt
    ? Math.round((new Date(attempt.expiresAt).getTime() - Date.now()) / 60_000)
    : null;

  return (
    <Panel title="Payment">
      <dl className="divide-y divide-rule">
        <Row label="Total">
          <span className="tabular">{formatMoney(data.totalCents, data.currency)}</span>
        </Row>

        <Row label="Payment">
          <Status value={attempt.status} />
        </Row>

        {attempt.paymentLink ? (
          <Row label="Link">
            <span className="text-small text-slate">
              {attempt.smsSentAt
                ? `Texted ${formatDateTime(attempt.smsSentAt)}`
                : attempt.smsDeliveryStatus === 'FAILED'
                  ? 'Text failed to send'
                  : 'Created, not yet sent'}
            </span>
          </Row>
        ) : null}

        {minutesLeft !== null && !['PAID', 'EXPIRED', 'CANCELLED'].includes(attempt.status) ? (
          <Row label="Expires">
            <span className="text-small text-slate">
              {minutesLeft > 0 ? `${minutesLeft} minutes remaining` : 'Expired'}
            </span>
          </Row>
        ) : null}

        {attempt.paidAt ? (
          <Row label="Paid">
            <span className="text-small text-ink">{formatDateTime(attempt.paidAt)}</span>
          </Row>
        ) : null}

        <Row label="Provider">
          <span className="text-small text-slate">{attempt.paymentProvider}</span>
        </Row>

        {/* Deliberately distinct from the payment status above. */}
        <Row label="POS ticket">
          <span className="tabular text-small text-slate">
            {data.externalPosOrderId ?? 'Not sent'}
          </span>
        </Row>

        <Row label="POS reconciliation">
          <span className="text-small text-slate">
            {reconciliationLabel[attempt.posReconciliationStatus ?? 'NOT_APPLICABLE'] ??
              attempt.posReconciliationStatus}
          </span>
        </Row>

        {data.externalPosOrderId ? (
          <Row label="POS order">
            <span className="tabular text-small text-slate">{data.externalPosOrderId}</span>
          </Row>
        ) : null}
      </dl>

      {attempt.paymentLink && attempt.status !== 'PAID' ? (
        <div className="border-t border-rule px-4 py-3">
          <Button onClick={resendLink} loading={isLoading}>
            Send the link again
          </Button>
          <p className="mt-1.5 text-micro text-slate">
            Sends the same link. It will never create a second payment request.
          </p>
        </div>
      ) : null}
    </Panel>
  );
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between px-4 py-2.5">
    <dt className="text-small text-slate">{label}</dt>
    <dd>{children}</dd>
  </div>
);
